<template>
    <div class="tab-detail">
        <div class="header">
            <div class="title">{{ $t("appStore.apps") }}</div>
            <div class="header-actions">
                <a-button type="primary" @click="syncApps" :loading="syncing">
                    <a-icon type="sync" /> {{ $t("appStore.syncApps") }}
                </a-button>
                <a-button @click="fetchApps" :loading="loading">
                    <a-icon type="reload" /> {{ $t("appStore.refresh") }}
                </a-button>
            </div>
        </div>
        <div class="help-text">
            <a-icon type="info-circle" /> {{ $t("appStore.appsAutoFetch") }}
        </div>
        <div class="apps-container" v-if="!loading">
            <div class="app-card" v-for="app in apps" :key="app._id">
                <div class="app-icon">
                    <img :src="app.iconUrl" v-if="app.iconUrl" />
                    <span class="material-icons" v-else>phone_iphone</span>
                </div>
                <div class="info">
                    <h3 class="app-name">{{ app.name }}</h3>
                    <div class="bundle-id">{{ app.bundleId }}</div>
                    <div class="meta">
                        <span v-if="app.totalReviewCount">
                            <span class="material-icons">reviews</span> {{ app.totalReviewCount }} reviews
                        </span>
                        <span v-if="app.primaryLocale">
                            <span class="material-icons">language</span> {{ app.primaryLocale }}
                        </span>
                        <span v-if="app.lastSyncedAt">
                            <span class="material-icons">sync</span> {{ formatDate(app.lastSyncedAt) }}
                        </span>
                    </div>
                </div>
                <div class="actions">
                    <a-button size="small" @click="syncReviews(app)" :loading="syncingApp === app._id">
                        <a-icon type="sync" /> {{ $t("appStore.syncReviews") }}
                    </a-button>
                </div>
            </div>
        </div>
        <div class="loading" v-else>
            <a-spin />
        </div>
        <div class="empty" v-if="!loading && apps.length === 0">
            <a-empty :description="$t('appStore.noAppsFound')">
                <a-button type="primary" @click="syncApps" :loading="syncing">
                    {{ $t("appStore.syncApps") }}
                </a-button>
            </a-empty>
        </div>
    </div>
</template>

<script>
import { APP_STORE_API } from "../../../../../api/appstore";
import moment from "moment";

export default {
    name: "AppList",
    props: {
        channel: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            apps: [],
            loading: false,
            syncing: false,
            syncingApp: null,
        };
    },
    methods: {
        async fetchApps() {
            this.loading = true;
            try {
                const response = await APP_STORE_API.listApps(this.channel._id);
                this.apps = response.data;
            } catch (err) {
                this.$message.error(this.$t("appStore.errorFetchingApps"));
            } finally {
                this.loading = false;
            }
        },
        async syncApps() {
            this.syncing = true;
            try {
                await APP_STORE_API.syncApps(this.channel._id);
                this.$message.success(this.$t("appStore.appsSynced"));
                await this.fetchApps();
            } catch (err) {
                var errorMessage = err.response && err.response.data && err.response.data.message;
                this.$message.error(errorMessage || this.$t("appStore.errorSyncingApps"));
            } finally {
                this.syncing = false;
            }
        },
        async syncReviews(app) {
            this.syncingApp = app._id;
            try {
                await APP_STORE_API.fetchReviews(this.channel._id, app.appId);
                this.$message.success(this.$t("appStore.reviewsSynced"));
                await this.fetchApps();
            } catch (err) {
                this.$message.error(this.$t("appStore.errorSyncingReviews"));
            } finally {
                this.syncingApp = null;
            }
        },
        formatDate(date) {
            return moment(date).format("DD.MM.YYYY HH:mm");
        },
    },
    mounted() {
        this.fetchApps();
    },
};
</script>

<style lang="scss" scoped>
.tab-detail {
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 20px;

    .header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding-bottom: 15px;
        border-bottom: 1px solid var(--color-border-default);

        .title {
            font-size: 18px;
            font-weight: 700;
            color: var(--color-text-strong-default);
        }

        .header-actions {
            display: flex;
            gap: 10px;
        }
    }

    .help-text {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 10px;
        background: var(--color-bg-subtle);
        border-radius: 6px;
        font-size: 13px;
        color: var(--color-text-default);
    }

    .apps-container {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
        gap: 24px;
        margin-top: 15px;

        .app-card {
            background: var(--color-bg-subtle);
            border-radius: 12px;
            padding: 20px;
            display: flex;
            gap: 16px;
            transition: transform 0.2s, box-shadow 0.2s;

            &:hover {
                transform: translateY(-2px);
                box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
            }

            .app-icon {
                width: 60px;
                height: 60px;
                background: #007aff;
                border-radius: 12px;
                display: flex;
                align-items: center;
                justify-content: center;
                flex-shrink: 0;
                overflow: hidden;

                img {
                    width: 100%;
                    height: 100%;
                    object-fit: cover;
                }

                .material-icons {
                    font-size: 28px;
                    color: #fff;
                }
            }

            .info {
                flex: 1;
                min-width: 0;

                .app-name {
                    margin: 0 0 4px;
                    font-size: 16px;
                    font-weight: 600;
                    color: var(--color-text-strong-default);
                }

                .bundle-id {
                    font-size: 12px;
                    color: var(--color-text-default);
                    font-family: monospace;
                    margin-bottom: 8px;
                }

                .meta {
                    display: flex;
                    gap: 16px;
                    flex-wrap: wrap;
                    color: var(--color-text-default);
                    font-size: 12px;

                    span {
                        display: flex;
                        align-items: center;
                        gap: 4px;

                        .material-icons {
                            font-size: 14px;
                        }
                    }
                }
            }

            .actions {
                display: flex;
                flex-direction: column;
                gap: 8px;
            }
        }
    }

    .loading,
    .empty {
        display: flex;
        justify-content: center;
        align-items: center;
        min-height: 300px;
    }
}
</style>
